import { Avatar, Box, useTheme } from "@mui/material";
import * as React from "react";

import { RobotoButton, RobotoTypography } from "@/shared/components";
import { OrgInviteRecord } from "@/shared/domain/invites";
import {
  useInviteSuspenseQuery,
  useAcceptInviteMutation,
  useDeclineInviteMutation,
} from "@/shared/domain/invites/hooks";
import { RobotoDomainException } from "@/shared/http";

import { InviteCard } from "./InviteCard";

interface InviteResponseFormProps {
  inviteId: string | undefined;
  onAccepted: (invite: OrgInviteRecord) => void;
  onDeclined: (invite: OrgInviteRecord) => void;
}

export const InviteResponseForm: React.FC<InviteResponseFormProps> = ({
  inviteId,
  onAccepted,
  onDeclined,
}) => {
  const theme = useTheme();

  const inviteQuery = useInviteSuspenseQuery(inviteId);
  const acceptInviteMutation = useAcceptInviteMutation();
  const declineInviteMutation = useDeclineInviteMutation();

  const onAcceptedWrapper = () => {
    // error monitoring handled in jsx below
    void acceptInviteMutation.mutateAsync(inviteId ?? "").then(() => {
      onAccepted(inviteQuery.data);
    });
  };

  const onDeclinedWrapper = () => {
    // error monitoring handled in jsx below
    void declineInviteMutation.mutateAsync(inviteId ?? "").then(() => {
      onDeclined(inviteQuery.data);
    });
  };

  let acceptErrorMessage: string | null = null;
  if (acceptInviteMutation.error instanceof RobotoDomainException) {
    acceptErrorMessage = acceptInviteMutation.error.message;
  } else if (acceptInviteMutation.error) {
    acceptErrorMessage = "Unable to accept invite. Please try again.";
  }

  let declineErrorMessage: string | null = null;
  if (declineInviteMutation.error instanceof RobotoDomainException) {
    declineErrorMessage = declineInviteMutation.error.message;
  } else if (declineInviteMutation.error) {
    declineErrorMessage = "Unable to decline invite. Please try again.";
  }

  return (
    <InviteCard>
      <RobotoTypography
        variant={"h6"}
        sx={{
          marginBottom: theme.spacing(2),
          display: "flex",
          justifyContent: "center",
          fontWeight: 500,
        }}
      >
        Join&nbsp;
        <span style={{ fontWeight: 700 }}>{inviteQuery.data?.org.name}</span>
        &nbsp;on&nbsp;
        <span style={{ fontWeight: 700 }}>Roboto</span>
      </RobotoTypography>
      <RobotoTypography
        sx={{
          marginBottom: theme.spacing(2),
          display: "flex",
          justifyContent: "center",
        }}
      >
        You were invited by:
      </RobotoTypography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: theme.spacing(2),
        }}
      >
        <Avatar
          src={inviteQuery?.data?.invited_by?.picture_url}
          sx={{ width: 30, height: 30, marginRight: theme.spacing(1.5) }}
          imgProps={{ referrerPolicy: "no-referrer" }}
        />

        <RobotoTypography>
          {inviteQuery?.data?.invited_by.user_id}
        </RobotoTypography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          mt: theme.spacing(4),
          mb: theme.spacing(1),
        }}
      >
        <RobotoButton
          eventName={"AcceptOrgInviteClicked"}
          eventProperties={{ inviteId: inviteId ?? "" }}
          onClick={() => {
            void onAcceptedWrapper();
          }}
          variant={"contained"}
          sx={{ width: "150px", marginRight: theme.spacing(2) }}
        >
          Accept
        </RobotoButton>
        <RobotoButton
          eventName={"DeclineOrgInviteClicked"}
          eventProperties={{ inviteId: inviteId ?? "" }}
          onClick={() => {
            void onDeclinedWrapper();
          }}
          variant={"outlined"}
          sx={{ width: "150px" }}
        >
          Decline
        </RobotoButton>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        {acceptInviteMutation.error && (
          <RobotoTypography
            variant={"body2"}
            sx={{ color: theme.palette.error.main }}
          >
            Error: {acceptErrorMessage}
          </RobotoTypography>
        )}

        {declineInviteMutation.error && (
          <RobotoTypography
            variant={"body2"}
            sx={{ color: theme.palette.error.main }}
          >
            Error: {declineErrorMessage}
          </RobotoTypography>
        )}
      </Box>
    </InviteCard>
  );
};
